import React from 'react';
import { Button, Result } from 'antd';
import { ClassifiedError } from '../../../../Utils/errorClassifier';
import { ERROR_MESSAGES } from '../../../../Utils/errorMessages';
import { FailedStep } from '../../../../Context/UserOnboardingContext';

interface WizardErrorPanelProps {
  classified: ClassifiedError;
  failedStep: FailedStep;
  isRecruiter?: boolean; // true for recruiter, false for candidate
  onRetry?: () => void;
  onUploadDifferentResume?: () => void;
  onSkip: () => void;
  onGoToDashboard?: () => void;
}

const WizardErrorPanel: React.FC<WizardErrorPanelProps> = ({
  classified,
  failedStep,
  isRecruiter = false,
  onRetry,
  onUploadDifferentResume,
  onSkip,
  onGoToDashboard,
}) => {
  const msgs = ERROR_MESSAGES[classified.category];
  const headline = isRecruiter ? msgs.recruiterHeadline : msgs.candidateHeadline;
  const body = isRecruiter ? msgs.recruiterBody : msgs.candidateBody;
  const guidance = isRecruiter ? msgs.recruiterGuidance : msgs.candidateGuidance;

  // Determine which buttons to show based on category and failedStep
  const isResumeFailure = ['resume_unreadable', 'resume_format', 'resume_language'].includes(
    classified.category
  );
  const isResumeStep = ['resume-upload', 'profile-matching'].includes(failedStep);

  // Button logic
  let primaryButton: React.ReactNode = null;
  let secondaryButton: React.ReactNode = null;

  if (classified.category === 'session_expired') {
    primaryButton = (
      <Button type="primary" onClick={() => {
        window.location.href = `/sign-in?redirect=${encodeURIComponent(window.location.pathname)}`;
      }}>
        Sign In Again
      </Button>
    );
  } else if (classified.category === 'access_denied') {
    secondaryButton = (
      <Button type="text" onClick={onGoToDashboard}>
        Go to Dashboard
      </Button>
    );
  } else if (isResumeFailure && isResumeStep) {
    // Resume failures on resume steps: "Upload Different Resume"
    primaryButton = (
      <Button type="primary" onClick={onUploadDifferentResume}>
        Upload Different Resume
      </Button>
    );
    secondaryButton = (
      <Button type="text" onClick={onSkip}>
        Skip for Now
      </Button>
    );
  } else {
    // All other cases: "Try Again"
    primaryButton = (
      <Button type="primary" onClick={onRetry}>
        Try Again
      </Button>
    );
    secondaryButton = (
      <Button type="text" onClick={onSkip}>
        Skip for Now
      </Button>
    );
  }

  return (
    <div className="uo-error-panel" style={{ margin: "24px auto", padding: '32px 24px', textAlign: 'center', maxWidth: "800px",  }}>
      <Result
        status="error"
        title={headline}
        subTitle={body}
        extra={[
          primaryButton && <div key="primary">{primaryButton}</div>,
          secondaryButton && <div key="secondary" style={{ marginTop: '12px' }}>{secondaryButton}</div>,
        ].filter(Boolean)}
      />
    </div>
  );
};

export default WizardErrorPanel;
